import { CredentialType } from '../../api/shared';

export interface JsonSchema {
  type: string;
  properties: Record<
    string,
    {
      type: string;
      description: string;
      default?: string;
    }
  >;
  required?: string[];
}

export const SFTP_SCHEMA: JsonSchema = {
  type: 'object',
  properties: {
    username: {
      type: 'string',
      description: 'SSH username for credential',
    },
    password: {
      type: 'string',
      description: 'SSH password for credential',
    },
    keyFilePath: {
      type: 'string',
      description: 'Relative path to SSH private key file (e.g., my-entity/id_rsa)',
      default: '',
    },
    keyPassphrase: {
      type: 'string',
      description: 'Passphrase to decrypt the private key, if encrypted',
    },
    knownHostsFile: {
      type: 'string',
      description: 'Relative path to known_hosts file (e.g., my-entity/known_hosts)',
      default: '',
    },
    knownHostsString: {
      type: 'string',
      description: 'Known hosts content as inline string (alternative to knownHostsFile)',
    },
    keyExchanges: {
      type: 'string',
      description: 'Comma-separated list of key exchange algorithms',
    },
    ciphers: {
      type: 'string',
      description: 'Comma-separated list of ciphers',
    },
    macs: {
      type: 'string',
      description: 'Comma-separated list of MAC algorithms',
    },
    hostKeyAlgorithms: {
      type: 'string',
      description: 'Comma-separated list of host key algorithms',
    },
    autoDisconnect: {
      type: 'string',
      description: 'Auto-disconnect timeout in seconds',
    },
    connectTimeout: {
      type: 'string',
      description: 'Connection timeout in seconds',
    },
    fileBufferSize: {
      type: 'string',
      description: 'File buffer size in bytes',
    },
    filePermissions: {
      type: 'string',
      description: 'File permissions (e.g., 0644)',
    },
  },
  required: ['knownHostsFile'],
};

export const AWS_SCHEMA: JsonSchema = {
  type: 'object',
  properties: {
    accessKeyId: {
      type: 'string',
      description: 'AWS Access Key ID',
    },
    secretAccessKey: {
      type: 'string',
      description: 'AWS Secret Access Key',
    },
    roleArn: {
      type: 'string',
      description: 'ARN of IAM role to assume',
    },
    region: {
      type: 'string',
      description: 'AWS region (e.g., us-east-1)',
    },
    sessionToken: {
      type: 'string',
      description: 'AWS session token (for temporary credentials)',
    },
    externalId: {
      type: 'string',
      description: 'External ID for assuming role',
    },
  },
};

export const GCP_SCHEMA: JsonSchema = {
  type: 'object',
  properties: {
    credentialFilePath: {
      type: 'string',
      description: 'Relative path to GCP service account JSON file (e.g., my-entity/gcp-sa.json)',
      default: '',
    },
  },
  required: ['credentialFilePath'],
};

export const AZURE_SCHEMA: JsonSchema = {
  type: 'object',
  properties: {
    accountName: {
      type: 'string',
      description: 'Azure Storage account name',
    },
    accountKey: {
      type: 'string',
      description: 'Azure Storage account access key',
    },
    tenantId: {
      type: 'string',
      description: 'Azure AD tenant ID',
    },
    clientId: {
      type: 'string',
      description: 'Azure AD application (client) ID',
    },
    clientSecret: {
      type: 'string',
      description: 'Azure AD client secret',
    },
    sasToken: {
      type: 'string',
      description: 'Azure SAS token',
    },
  },
};

export const getSchemaForCredentialType = (type: CredentialType): JsonSchema => {
  switch (type) {
    case CredentialType.CREDENTIAL_TYPE_SSH:
      return SFTP_SCHEMA;
    case CredentialType.CREDENTIAL_TYPE_AWS:
      return AWS_SCHEMA;
    case CredentialType.CREDENTIAL_TYPE_GCP_SERVICE_ACCOUNT:
      return GCP_SCHEMA;
    case CredentialType.CREDENTIAL_TYPE_AZURE_SERVICE_PRINCIPAL:
      return AZURE_SCHEMA;
    default:
      return { type: 'object', properties: {} };
  }
};

export const getAllFieldsForCredentialType = (type: CredentialType): Record<string, string> => {
  const schema = getSchemaForCredentialType(type);

  return Object.keys(schema.properties).reduce(
    (acc, key) => {
      acc[key] = '';

      return acc;
    },
    {} as Record<string, string>,
  );
};
